import { ComponentProps, FC, ReactNode, useId } from "react";
import * as Slot from "@rn-primitives/slot";
import {
  Control,
  Controller,
  ControllerProps,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  FormProvider,
  Path,
  UseFormReturn,
} from "react-hook-form";

import { FormFieldContext } from "@/context/FormFieldProvider";
import { FormItemContext } from "@/context/FormItemProvider";
import { useFormField } from "@/hooks/useFormField";
import { usePlatformOS } from "@/hooks/usePlatformOS";
import { useTranslate } from "@/hooks/useTranslate";
import { I18nKeyType } from "@/i18n";
import { cn } from "@/utils/tailwind";

import { VnBox } from "./VnBox";
import { VnLabel } from "./VnLabel";
import { textVariants } from "./VnText/variants";
import { VnText, VnTextPropsType } from "./VnText/VnText";

type VnFormPropsType<T extends FieldValues> = Omit<
  ComponentProps<"form">,
  "onSubmit"
> & {
  form: UseFormReturn<T>;
  className?: string;
  isInputOnlyForm?: boolean;
  onSubmit?: (values: T) => void;
} & (
    | {
        isInputOnlyForm: true;
        onSubmit?: never;
      }
    | {
        isInputOnlyForm?: false;
        onSubmit: (values: T) => void;
      }
  );

export const VnForm = <T extends FieldValues>({
  form,
  children,
  onSubmit,
  className,
  isInputOnlyForm = false,
  ...props
}: VnFormPropsType<T>) => {
  const { isNative } = usePlatformOS();

  if (isNative || isInputOnlyForm) {
    return (
      <FormProvider {...form}>
        <VnBox className={cn("w-full gap-4", className)}>{children}</VnBox>
      </FormProvider>
    );
  }

  return (
    <FormProvider {...form}>
      <form
        className={cn("flex w-full flex-col gap-6", className)}
        onSubmit={event => {
          if (onSubmit) {
            void form.handleSubmit(onSubmit)();
          }
          event.preventDefault();
        }}
        {...props}>
        {children}
      </form>
    </FormProvider>
  );
};

export const VnFormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: ControllerProps<TFieldValues, TName>,
) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

export const VnFormItem: FC<ComponentProps<typeof VnBox>> = props => {
  const id = useId();

  return (
    <FormItemContext.Provider value={{ id }}>
      <VnBox data-slot="form-item" {...props} />
    </FormItemContext.Provider>
  );
};

type VnLabelPropsType = ComponentProps<typeof VnLabel> & {
  isDisabled?: boolean;
  isRequired?: boolean;
};

export const VnFormLabel: FC<VnLabelPropsType> = ({
  className,
  isDisabled,
  isRequired,
  ...props
}) => {
  const { error, formItemId } = useFormField();

  return (
    <VnBox className="shrink flex-row">
      <VnLabel
        data-slot="form-label"
        data-error={Boolean(error)}
        disabled={isDisabled}
        className={cn(
          {
            "cursor-not-allowed opacity-50": isDisabled,
          },
          className,
        )}
        htmlFor={formItemId}
        {...props}
      />
      {isRequired && (
        <VnText className="text-destructive mb-0.5 ml-0.5" variant="sm">
          *
        </VnText>
      )}
    </VnBox>
  );
};

export const VnFormControl: FC<ComponentProps<typeof Slot.View>> = props => {
  const { error, formItemId, formDescriptionId, formMessageId } =
    useFormField();

  return (
    <Slot.View
      data-slot="form-control"
      id={formItemId}
      aria-describedby={
        !error
          ? `${formDescriptionId}`
          : `${formDescriptionId} ${formMessageId}`
      }
      aria-invalid={Boolean(error)}
      {...props}
    />
  );
};

export const VnFormDescription: FC<VnTextPropsType> = ({
  className,
  children,
  i18nKey,
  i18nOptions,
  ...props
}) => {
  const { formDescriptionId } = useFormField();
  const i18nText = useTranslate(i18nKey, i18nOptions);

  return (
    <VnText
      data-slot="form-description"
      id={formDescriptionId}
      className={cn(
        textVariants(),
        "text-muted-foreground whitespace-pre-line",
        className,
      )}
      {...props}>
      {children ?? i18nText}
    </VnText>
  );
};

export const VnFormErrorMessage: FC<VnTextPropsType> = ({
  className,
  children,
  i18nKey,
  i18nOptions,
  ...props
}) => {
  const { error } = useFormField();
  const i18nText = useTranslate(i18nKey, i18nOptions);

  const body = error
    ? String(error?.message ?? error?.root?.message ?? "")
    : (i18nText ?? children);

  const hasMessage = Boolean(body);

  return (
    <VnBox
      className={cn(
        "max-h-0 overflow-hidden whitespace-pre-line transition-[max-height] duration-200 ease-in-out",
        {
          "mt-2 max-h-20": hasMessage,
        },
      )}>
      <VnText data-slot="form-message" className="text-destructive" {...props}>
        {body}
      </VnText>
    </VnBox>
  );
};

type VnFormInputPropsType<T extends FieldValues> = {
  name: FieldPath<T>;
  control: Control<T>;
  isDisabled?: boolean;
  shouldApplyDisabledLabelStyle?: boolean;
  labelI18nKey?: I18nKeyType;
  labelI18nOptions?: object;
  descriptionI18nKey?: I18nKeyType;
  descriptionI18nOptions?: object;
  showError?: boolean;
  isRequired?: boolean;
  errorI18nKey?: I18nKeyType;
  errorI18nOptions?: object;
  className?: string;
  children: ({
    field,
    hasError,
  }: {
    field: Omit<ControllerRenderProps<T, Path<T>>, "ref">;
    hasError: boolean;
  }) => ReactNode;
};

export type BaseFormInputPropsType<T extends FieldValues> = Omit<
  VnFormInputPropsType<T>,
  "children"
>;

export const VnFormInput = <T extends FieldValues>({
  name,
  control,
  isDisabled = false,
  shouldApplyDisabledLabelStyle = true,
  labelI18nKey,
  labelI18nOptions,
  descriptionI18nKey,
  descriptionI18nOptions,
  isRequired = true,
  showError = true,
  errorI18nKey,
  errorI18nOptions,
  className,
  children,
}: VnFormInputPropsType<T>) => {
  return (
    <VnFormField
      name={name}
      control={control}
      render={({ field: { ref, ...field }, fieldState: { error } }) => (
        <VnFormItem>
          <VnBox className={cn("gap-2", className)}>
            {labelI18nKey && (
              <VnFormLabel
                i18nKey={labelI18nKey}
                i18nOptions={labelI18nOptions}
                isDisabled={isDisabled && shouldApplyDisabledLabelStyle}
                isRequired={isRequired}
              />
            )}
            <VnFormControl>
              {children({ field, hasError: Boolean(error?.message) })}
            </VnFormControl>
            {descriptionI18nKey && (
              <VnFormDescription
                i18nKey={descriptionI18nKey}
                i18nOptions={descriptionI18nOptions}
              />
            )}
          </VnBox>
          {showError && (
            <VnFormErrorMessage
              i18nKey={errorI18nKey}
              i18nOptions={errorI18nOptions}
            />
          )}
        </VnFormItem>
      )}
    />
  );
};
